import React, { useEffect, useState, useCallback } from "react";
import { Typography, Button, Chip, CircularProgress } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// feat_210 estándar — "Modo de carga de datos" de una empresa (superadmin,
// pestaña Carga de Datos del CompanyEditModal). Estados del backend:
//   none     → la empresa no usa el Excel estándar.
//   standard → un slot apunta al perfil "Estándar" (Telefono/Nombre/Dato1-3).
//   custom   → un slot apunta a la copia "A medida" (se edita en Envío Masivo).
// Textos en español, como el resto de las pantallas de superadmin.

const ERRORS = {
  ERR_NO_FREE_SLOT:
    "No hay un slot libre: los 4 slots de Carga de Datos están ocupados. Liberá uno (borralo o cambialo) y volvé a intentar.",
  ERR_STANDARD_NOT_ENABLED:
    "Primero hay que habilitar el Excel estándar.",
};

const showError = (err) => {
  const code = err?.response?.data?.error || err?.response?.data?.message;
  if (code && ERRORS[code]) {
    toast.error(ERRORS[code], { toastId: code });
    return;
  }
  toastError(err);
};

const StandardBulkModeCard = ({ companyId, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/companies/${companyId}/standard-bulk-profile`);
      setStatus(data);
    } catch (err) {
      setStatus(null);
      toastError(err);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const change = async (mode, confirmText, okText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/superadmin/companies/${companyId}/standard-bulk-profile`,
        { mode }
      );
      setStatus(data);
      toast.success(okText);
      if (onChanged) onChanged();
    } catch (err) {
      showError(err);
    }
    setSaving(false);
  };

  const state = status?.state;
  const slotLabel = status?.slot
    ? `Slot ${status.slot}${status.slotEnabled === false ? " (deshabilitado)" : ""}`
    : null;

  return (
    <div style={{ marginBottom: 20, padding: "12px 16px", border: "1px solid #c7d2fe", borderRadius: 8, background: "#eef2ff" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          Modo de carga de datos
        </Typography>
        {loading ? (
          <CircularProgress size={16} />
        ) : state ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Chip
              size="small"
              color={state === "none" ? "default" : "primary"}
              label={state === "standard" ? "Excel estándar" : state === "custom" ? "Excel a medida" : "Sin Excel estándar"}
            />
            {slotLabel && <Typography variant="caption" color="textSecondary">{slotLabel}</Typography>}
          </div>
        ) : null}
      </div>

      {state === "none" && (
        <>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
            Excel estándar: el cliente baja un modelo con las columnas Telefono, Nombre, Dato1, Dato2 y Dato3, y la
            plantilla que elige define cuántas variables se usan. Al habilitarlo se crea el perfil «Estándar» y se
            asocia al primer slot libre (no se pisa ningún slot ocupado).
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={saving}
            onClick={() => change("standard", null, "Excel estándar habilitado")}
          >
            {saving ? "Guardando..." : "Habilitar Excel estándar"}
          </Button>
        </>
      )}

      {state === "standard" && (
        <>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
            El cliente ve el link «Descargar modelo de Excel» en Carga de Datos. Si el cliente necesita columnas
            propias, pasalo a Excel a medida: se crea una copia «A medida» del perfil estándar para editarla.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={saving}
            onClick={() => change(
              "custom",
              "¿Pasar a Excel a medida? El slot va a usar una copia editable del perfil estándar y el cliente deja de ver el modelo descargable.",
              "Listo: editá el perfil «A medida» en la pestaña Envío Masivo"
            )}
          >
            {saving ? "Guardando..." : "Pasar a Excel a medida"}
          </Button>
        </>
      )}

      {state === "custom" && (
        <>
          <Typography variant="caption" style={{ display: "block", marginBottom: 8 }}>
            Editá el perfil «A medida» en la pestaña <b>Envío Masivo</b> (editor JSON). Al volver al estándar, la copia
            queda guardada (inactiva) con sus cambios.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={saving}
            onClick={() => change(
              "standard",
              "¿Volver al Excel estándar? El slot vuelve al perfil «Estándar»; la copia «A medida» se desactiva pero no se borra.",
              "Excel estándar restablecido"
            )}
          >
            {saving ? "Guardando..." : "Volver al estándar"}
          </Button>
        </>
      )}
    </div>
  );
};

export default StandardBulkModeCard;
